import { authStorage } from './authStorage';
import { refreshAuthToken } from './coreApi';
import type {
  Profile,
  ProfileFormData,
  ManagerDashboardStats,
  ManagerProfileFormData,
  Qualification,
  QualificationFormData,
  Verification,
  VerificationFormData,
  Document,
  DocumentFormData,
  WorkExperience,
  WorkExperienceFormData,
  ProfessionalReference,
  ReferenceFormData,
  PublicPageSettings,
  WorkerPublicPage,
  PublicPassportResponse,
  ShareableField,
  RecordKind,
  QueueItem,
  ItemVerificationStatus,
} from '../types';

const BASE_URL = (import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3001').replace(/\/$/, '');

export class BackendApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'BackendApiError';
  }
}

// Singleton refresh promise — prevents parallel refresh calls from invalidating each other
let refreshPromise: Promise<void> | null = null;

async function attemptTokenRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = authStorage.getRefreshToken();
    if (!refreshToken) {
      authStorage.clearSession();
      window.location.href = '/login';
      return;
    }
    try {
      const data = await refreshAuthToken(refreshToken);
      authStorage.updateTokens(data.accessToken, data.expiresIn, data.refreshToken);
    } catch {
      authStorage.clearSession();
      window.location.href = '/login';
    }
  })();

  try {
    await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  // Proactively refresh if the access token expires within 5 minutes
  const expiresAt = authStorage.getAccessTokenExpiresAt();
  if (expiresAt > 0 && Date.now() > expiresAt - 5 * 60 * 1000) {
    await attemptTokenRefresh();
  }

  const token = authStorage.getToken();

  const headers: Record<string, string> = {
    ...(options.body && !(options.body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(options.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let response = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  // On 401, attempt one token refresh then retry
  if (response.status === 401) {
    await attemptTokenRefresh();
    const newToken = authStorage.getToken();
    const retryHeaders: Record<string, string> = {
      ...headers,
      ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
    };
    response = await fetch(`${BASE_URL}${path}`, { ...options, headers: retryHeaders });
  }

  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = await response.json();
      message = body.message ?? message;
    } catch {
      // ignore parse errors
    }
    throw new BackendApiError(message, response.status);
  }

  if (response.status === 204) return undefined as unknown as T;
  const json = await response.json();
  return (json.data ?? json) as T;
}

// =====================================================
// PROFILE
// =====================================================

function toProfileApiBody(data: Partial<ProfileFormData & ManagerProfileFormData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.first_name !== undefined) body.firstName = data.first_name;
  if (data.last_name !== undefined) body.lastName = data.last_name;
  if (data.bio !== undefined) body.bio = data.bio;
  if (data.field_of_work !== undefined) body.fieldOfWork = data.field_of_work;
  if (data.phone !== undefined) body.phone = data.phone;
  if (data.contact_email !== undefined) body.contactEmail = data.contact_email;
  if (data.rsa_id_number !== undefined) body.rsaIdNumber = data.rsa_id_number;
  if (data.passport_no !== undefined) body.passportNo = data.passport_no;
  if (data.company_name !== undefined) body.companyName = data.company_name;
  if (data.industry !== undefined) body.industry = data.industry;
  if (data.company_size !== undefined) body.companySize = data.company_size;
  if (data.company_website !== undefined) body.companyWebsite = data.company_website;
  return body;
}

export const profileApi = {
  getMe: () => apiFetch<Profile>('/profile/me'),

  updateMe: (data: Partial<ProfileFormData & ManagerProfileFormData>) =>
    apiFetch<Profile>('/profile/me', {
      method: 'PATCH',
      body: JSON.stringify(toProfileApiBody(data)),
    }),

  uploadPhoto: (file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return apiFetch<{ photoUrl: string }>('/profile/me/photo', {
      method: 'POST',
      body: form,
    });
  },

  deletePhoto: () =>
    apiFetch<void>('/profile/me/photo', { method: 'DELETE' }),

  getWorkers: (params?: {
    fieldOfWork?: string;
    name?: string;
    tenantId?: string;
    siteId?: string;
    page?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return apiFetch<Profile[]>(`/profile/workers${qs ? `?${qs}` : ''}`);
  },

  getDashboardStats: () => apiFetch<ManagerDashboardStats>('/profile/dashboard-stats'),

  getById: (id: string) => apiFetch<Profile>(`/profile/${id}`),

  getPublicPage: () => apiFetch<PublicPageSettings>('/profile/me/public-page'),

  updatePublicPage: (data: { publicVisible?: boolean; publicFields?: ShareableField[] }) =>
    apiFetch<PublicPageSettings>('/profile/me/public-page', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getWorkerPublicPages: () => apiFetch<WorkerPublicPage[]>('/profile/workers/public-pages'),
};

async function publicApiFetch<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`);
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = await response.json();
      message = body.message ?? message;
    } catch {
      // ignore parse errors
    }
    throw new BackendApiError(message, response.status);
  }
  const json = await response.json();
  return (json.data ?? json) as T;
}

export const publicPassportApi = {
  getByToken: (token: string) =>
    publicApiFetch<PublicPassportResponse>(`/public/passports/${encodeURIComponent(token)}`),
};

// =====================================================
// QUALIFICATIONS
// =====================================================

function toQualificationApiBody(data: Partial<QualificationFormData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.title !== undefined) body.title = data.title;
  if (data.description !== undefined) body.description = data.description;
  if (data.institution !== undefined) body.institution = data.institution;
  if (data.qualification_type !== undefined) body.qualificationType = data.qualification_type;
  if (data.field_of_work) body.fieldOfWork = data.field_of_work;
  if (data.issue_date) body.issueDate = data.issue_date;
  if (data.expiry_date) body.expiryDate = data.expiry_date;
  if (data.credential_id) body.credentialId = data.credential_id;
  if (data.credential_url) body.credentialUrl = data.credential_url;
  return body;
}

export const qualificationsApi = {
  getAll: () => apiFetch<Qualification[]>('/qualifications'),

  create: (data: QualificationFormData) =>
    apiFetch<Qualification>('/qualifications', {
      method: 'POST',
      body: JSON.stringify(toQualificationApiBody(data)),
    }),

  update: (id: string, data: Partial<QualificationFormData>) =>
    apiFetch<Qualification>(`/qualifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(toQualificationApiBody(data)),
    }),

  remove: (id: string) => apiFetch<void>(`/qualifications/${id}`, { method: 'DELETE' }),

  getForWorker: (workerId: string) =>
    apiFetch<Qualification[]>(`/qualifications/user/${workerId}`),
};

// =====================================================
// VERIFICATIONS
// =====================================================

function toVerificationCreateBody(data: VerificationFormData): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.user_id !== undefined) body.userId = data.user_id;
  if (data.verification_type !== undefined) body.verificationType = data.verification_type;
  if (data.qualification_id) body.qualificationId = data.qualification_id;
  if (data.message !== undefined) body.message = data.message;
  if (data.notes !== undefined) body.notes = data.notes;
  if (data.employer_name !== undefined) body.employerName = data.employer_name;
  return body;
}

function toVerificationUpdateBody(data: Partial<VerificationFormData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.status !== undefined) body.status = data.status;
  if (data.message !== undefined) body.message = data.message;
  if (data.notes !== undefined) body.notes = data.notes;
  if (data.responded_at !== undefined) body.respondedAt = data.responded_at;
  return body;
}

export const verificationsApi = {
  getAll: () => apiFetch<Verification[]>('/verifications'),

  create: (data: VerificationFormData) =>
    apiFetch<Verification>('/verifications', {
      method: 'POST',
      body: JSON.stringify(toVerificationCreateBody(data)),
    }),

  verifyExistingEvidence: (qualificationId: string, documentId: string) =>
    apiFetch<Verification>('/verifications/direct', {
      method: 'POST',
      body: JSON.stringify({ qualificationId, documentId }),
    }),

  update: (id: string, data: Partial<VerificationFormData>) =>
    apiFetch<Verification>(`/verifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(toVerificationUpdateBody(data)),
    }),

  remove: (id: string) => apiFetch<void>(`/verifications/${id}`, { method: 'DELETE' }),
};

// =====================================================
// DOCUMENTS
// =====================================================

export const documentsApi = {
  getAll: () => apiFetch<Document[]>('/documents'),

  getForWorker: (userId: string) => apiFetch<Document[]>(`/documents/user/${userId}`),

  getForVerification: (verificationId: string) =>
    apiFetch<Document[]>(`/documents/verification/${verificationId}`),

  upload: (file: File, data: Omit<DocumentFormData, 'file_name' | 'file_url' | 'file_size' | 'mime_type'>) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentType', data.document_type);
    if (data.qualification_id) form.append('qualificationId', data.qualification_id);
    if (data.reference_id) form.append('referenceId', data.reference_id);
    if (data.verification_id) form.append('verificationId', data.verification_id);
    return apiFetch<Document>('/documents', { method: 'POST', body: form });
  },

  verify: (id: string) =>
    apiFetch<Document>(`/documents/${id}/verify`, { method: 'POST' }),

  remove: (id: string) => apiFetch<void>(`/documents/${id}`, { method: 'DELETE' }),
};

// =====================================================
// WORK EXPERIENCE
// =====================================================

function toWorkExperiencePayload(data: Partial<WorkExperienceFormData>) {
  return {
    ...(data.job_title && { jobTitle: data.job_title }),
    ...(data.employer && { employer: data.employer }),
    ...(data.location && { location: data.location }),
    ...(data.start_date && { startDate: data.start_date }),
    ...(data.end_date && { endDate: data.end_date }),
    ...(data.is_current !== undefined && { isCurrent: data.is_current }),
    ...(data.description && { description: data.description }),
    ...(data.reason_for_leaving !== undefined && { reasonForLeaving: data.reason_for_leaving }),
    ...(data.field_of_work && { fieldOfWork: data.field_of_work }),
    ...(data.company_website && { companyWebsite: data.company_website }),
  };
}

export const workExperienceApi = {
  getAll: () => apiFetch<WorkExperience[]>('/work-experience'),

  getForWorker: (userId: string) => apiFetch<WorkExperience[]>(`/work-experience/user/${userId}`),

  create: (data: WorkExperienceFormData) =>
    apiFetch<WorkExperience>('/work-experience', {
      method: 'POST',
      body: JSON.stringify(toWorkExperiencePayload(data)),
    }),

  update: (id: string, data: Partial<WorkExperienceFormData>) =>
    apiFetch<WorkExperience>(`/work-experience/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(toWorkExperiencePayload(data)),
    }),

  remove: (id: string) => apiFetch<void>(`/work-experience/${id}`, { method: 'DELETE' }),
};

// =====================================================
// REFERENCES
// =====================================================

function toReferenceApiBody(data: Partial<ReferenceFormData>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (data.reference_name !== undefined) body.referenceName = data.reference_name;
  if (data.reference_email !== undefined) body.referenceEmail = data.reference_email;
  if (data.reference_phone !== undefined) body.referencePhone = data.reference_phone;
  if (data.relationship !== undefined) body.relationship = data.relationship;
  if (data.company !== undefined) body.company = data.company;
  if (data.job_title !== undefined) body.jobTitle = data.job_title;
  if (data.notes !== undefined) body.notes = data.notes;
  return body;
}

export const referencesApi = {
  getAll: () => apiFetch<ProfessionalReference[]>('/references'),

  getForWorker: (userId: string) => apiFetch<ProfessionalReference[]>(`/references/user/${userId}`),

  create: (data: ReferenceFormData) =>
    apiFetch<ProfessionalReference>('/references', {
      method: 'POST',
      body: JSON.stringify(toReferenceApiBody(data)),
    }),

  update: (id: string, data: Partial<ReferenceFormData>) =>
    apiFetch<ProfessionalReference>(`/references/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(toReferenceApiBody(data)),
    }),

  remove: (id: string) => apiFetch<void>(`/references/${id}`, { method: 'DELETE' }),
};

export const recordsApi = {
  confirm: (kind: RecordKind, id: string) =>
    apiFetch<Record<string, unknown>>(`/records/${kind}/${id}/confirm`, { method: 'POST' }),

  verify: (kind: RecordKind, id: string, basis: string) =>
    apiFetch<Record<string, unknown>>(`/records/${kind}/${id}/verify`, {
      method: 'POST',
      body: JSON.stringify({ basis }),
    }),

  queue: (params?: {
    tenantId?: string;
    siteId?: string;
    name?: string;
    status?: ItemVerificationStatus;
  }) => {
    const qs = new URLSearchParams(
      Object.entries(params ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return apiFetch<QueueItem[]>(`/records/verifications/queue${qs ? `?${qs}` : ''}`);
  },
};
